import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viPlus, viWebhooks } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function WebhookList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const webhookStore = useSelector((state) => state.domain.webhooks);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time webhookList or searchQuery changes
  const searchResults = useSearch(webhookStore.list, searchValue, ["name"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getWebhooks()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && webhookStore.meta.lastFetched) {
      if (webhookStore.list.length >= 1) {
        navigate(`${webhookStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, webhookStore.list, webhookStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Webhooks"
        aria-label="Webhooks list"
        id="webhook-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => (x.useNewWebhooks ? undefined : "Legacy webhook"),
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viWebhooks,
        }}
        hideContentLoader={webhookStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: webhookStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any webhooks yet",
          noResults: "No webhooks in your current query",
          fetching: "Getting your webhooks",
          icon: viWebhooks,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default WebhookList;
